import type { INotificationRule } from '@condo/domain';
import { NotificationTemplateMap } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    deleteNotificationRule as deleteNotificationRuleApi,
    deleteNotificationRuleForUser,
    generateNotificationRule as generateNotificationRuleApi,
    getNotificationRuleTemplates,
    getUserNotificationRules,
    updateNotificationRule as updateNotificationRuleApi,
} from '../api/notifications.api';
import { withGlobalErrorHandling, withGlobalMutationErrorHandling } from '@/shared/lib/query-client';

export type { INotificationTemplateWithRules } from './notification-settings.types';

export const notificationQueryKeys = {
    all: ['user', 'notification-templates-with-rules'] as const,
    templatesWithRules: (userUid: string | null) => [...notificationQueryKeys.all, userUid] as const,
};

/** Fetch templates and rules for a user, enriched with rule counts */
export function useNotificationTemplatesWithRules(userUid: string | null, userEmail?: string) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: notificationQueryKeys.templatesWithRules(userUid),
        queryFn: async () => {
            const [templates, rules] = await Promise.all([getNotificationRuleTemplates(), userUid ? getUserNotificationRules(userUid) : []]);

            return templates
                .map(template => ({
                    ...template,
                    title: NotificationTemplateMap[template.name]?.title ?? template.name,
                    description: NotificationTemplateMap[template.name]?.description ?? template.name,
                    rules: rules
                        .filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId)
                        .sort((a, b) => Number(b.active) - Number(a.active)),
                    ruleCount: rules.filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId).length,
                    activeRuleCount: rules.filter(rule => rule.notificationRuleTemplateId === template.notificationRuleTemplateId && rule.active).length,
                }))
                .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
        },
        enabled: !!userUid,
        ...withGlobalErrorHandling(),
    });

    const deleteRuleMutation = useMutation({
        mutationFn: (notificationRuleId: number) => deleteNotificationRuleApi(notificationRuleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationQueryKeys.templatesWithRules(userUid) });
        },
        ...withGlobalMutationErrorHandling(),
    });

    const deleteRulesForUserMutation = useMutation({
        mutationFn: (uid: string) => deleteNotificationRuleForUser(uid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationQueryKeys.templatesWithRules(userUid) });
        },
        ...withGlobalMutationErrorHandling(),
    });

    const updateRuleMutation = useMutation({
        mutationFn: (rule: INotificationRule) => updateNotificationRuleApi(rule),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationQueryKeys.templatesWithRules(userUid) });
        },
        ...withGlobalMutationErrorHandling(),
    });

    const generateRuleMutation = useMutation({
        mutationFn: (params: { rules: { templateName: string; tokens: Record<string, unknown> }[]; userUid?: string; userEmail?: string }) =>
            generateNotificationRuleApi(
                params.rules,
                (params.userUid ?? userUid) ? { uid: params.userUid ?? userUid!, email: params.userEmail ?? userEmail } : undefined,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationQueryKeys.templatesWithRules(userUid) });
        },
        ...withGlobalMutationErrorHandling(),
    });

    return {
        ...query,
        deleteNotificationRule: deleteRuleMutation.mutateAsync,
        deleteNotificationRulesForUser: deleteRulesForUserMutation.mutateAsync,
        updateNotificationRule: updateRuleMutation.mutateAsync,
        generateNotificationRule: generateRuleMutation.mutateAsync,
    };
}
